export const mobileArticles = [
    {
        id: 'features.mobile',
        title: 'Mobile & Tablet Access',
        category: 'Getting Started',
        order: 10,
        summary: 'Using Costed.app on phones and tablets',
        content: `
Costed.app is fully responsive and works on phones, tablets, and desktops. The interface adapts automatically to your screen size.

**Mobile Features:**

- Full-screen slide-out navigation menu
- Touch-optimised grids and forms
- Swipe gestures for document viewing
- Mobile-friendly tab dropdowns
- Responsive data grids that hide non-essential columns

**Progressive Web App (PWA):**

Costed.app can be installed on your device like a native app:
- Works offline for viewing cached data
- Launches from your home screen
- Full-screen experience without browser bars
    `,
        expandableSections: [
            {
                title: 'Navigation on Mobile',
                content: `
**Opening the Menu:**
Tap the hamburger icon (three lines) in the top-left corner to open the navigation drawer.

**Key Differences from Desktop:**
- Sidebar becomes a slide-out drawer
- Page tabs become dropdown selectors
- Data grids show fewer columns (essential fields only)
- Actions move to compact toolbars
- BOQ page is hidden on mobile (desktop-only due to complexity)

**Tip:** The navigation drawer closes automatically when you select a page.
        `,
            },
            {
                title: 'Installing as a PWA',
                content: `
**On iPhone/iPad (Safari):**
1. Open Costed.app in Safari
2. Tap the Share button (square with arrow)
3. Scroll down and tap **Add to Home Screen**
4. Tap **Add**

**On Android (Chrome):**
1. Open Costed.app in Chrome
2. Tap the three-dot menu
3. Tap **Install app** or **Add to Home Screen**
4. Tap **Install**

**On Desktop (Chrome/Edge):**
1. Look for the install icon in the address bar
2. Click **Install**

Once installed, the app opens in its own window and appears in your app launcher.
        `,
            },
            {
                title: 'Mobile Grid Behaviour',
                content: `
Data grids adapt on small screens:

- **Column Hiding** - Less important columns are hidden automatically
- **Column Priorities** - Key columns (Name, Amount, Status) always show
- **Horizontal Scroll** - Swipe left/right to see more columns
- **Row Actions** - Long-press or tap to access row menus
- **Full-width Layout** - Grids use all available screen space

**Tip:** Rotate to landscape for more columns on tablets.
        `,
            },
            {
                title: 'Document Viewing on Mobile',
                content: `
The document viewer is touch-optimised:

- **Pinch to Zoom** - Standard pinch gesture on PDFs and images
- **Swipe** - Navigate between pages
- **Double-tap** - Zoom in/out quickly
- **Rotate** - Landscape mode for wider documents

PDF performance is optimised for mobile with progressive page loading.
        `,
            },
            {
                title: 'Pages Available on Mobile',
                content: `
All pages except BOQ are available on mobile:

| Page | Mobile Support |
|------|---------------|
| Dashboard | Full |
| Jobs | Full (compact cards) |
| Projects | Full |
| Catalogue | Full |
| Orders | Full (full-screen detail view) |
| RFQs | Full |
| Suppliers | Full |
| Contacts | Full |
| Accounting | Full (tab dropdown) |
| Documents | Full (touch viewer) |
| Transmittals | Full |
| Reports | Full |
| Recycle Bin | Full |
| Settings | Full |
| BOQ | Desktop only |
        `,
            },
        ],
        keywords: ['mobile', 'tablet', 'phone', 'responsive', 'touch', 'PWA', 'offline', 'install'],
        relatedArticles: ['getting-started.welcome', 'features.keyboard-shortcuts'],
        screenshots: [
            {
                src: 'mobile/mobile-dashboard.png',
                alt: 'Dashboard on mobile',
                caption: 'Dashboard view optimised for mobile screens',
            },
            {
                src: 'mobile/mobile-jobs.png',
                alt: 'Jobs page on mobile',
                caption: 'Jobs list with responsive grid on mobile',
            },
            {
                src: 'mobile/mobile-contacts.png',
                alt: 'Contacts on mobile',
                caption: 'Contacts page adapted for touch interaction',
            },
            {
                src: 'mobile/mobile-accounting.png',
                alt: 'Accounting on mobile',
                caption: 'Accounting page with tab dropdown on mobile',
            },
        ],
    },
    {
        id: 'features.dark-mode',
        title: 'Dark Mode',
        category: 'Getting Started',
        order: 11,
        summary: 'Switching between light, dark, and system themes',
        content: `
Costed.app supports three theme modes for comfortable viewing in any environment.

**Theme Options:**

| Mode | Description |
|------|-------------|
| **Light** | Standard light background |
| **Dark** | Dark background, reduced eye strain |
| **System** | Follows your device's dark mode setting |

**Switching Themes:**

Click the theme toggle button in the header (sun/moon/half-circle icon). Each click cycles through: Light > Dark > System.

The theme applies immediately across all pages, including:
- Navigation sidebar
- Data grids
- Forms and modals
- Document viewer
- Charts and dashboards

**Note:** Your theme preference is saved and persists across sessions.
    `,
        keywords: ['dark mode', 'theme', 'light mode', 'system theme', 'appearance'],
        relatedArticles: ['features.mobile'],
    },
];
